import {
  useMutation,
  useQuery,
  useQueryClient,
  useIsRestoring,
} from "@tanstack/react-query";
import { useAuth } from "~/lib/auth";
import { backendConfigured } from "~/lib/constants";
import { GUEST_TIMERS_QUERY_KEY, type GuestTimer } from "~/lib/guest-data";
import { recipeTimersQueryKey } from "~/lib/query-client";
import type { Recipe, RecipeStep, RecipeTimer } from "~/types/models";
import * as Crypto from "expo-crypto";
import { unwrap } from "@brad-tipper/app-frontend";
import { api } from "~/api/client";
import type { RecipeTimer as ApiRecipeTimer } from "~/api/generated/types.gen";

export type { RecipeTimer };

function mapTimer(row: ApiRecipeTimer): RecipeTimer {
  return {
    id: row.id,
    userId: row.owner_id,
    recipeId: row.recipe_id,
    recipeTitle: row.recipe_title,
    recipeEmoji: row.recipe_emoji,
    stepIndex: row.step_index,
    stepTitle: row.step_title,
    startedAt: row.started_at,
    notifyAt: row.notify_at,
    status: row.status,
  };
}

function guestToTimer(guest: GuestTimer): RecipeTimer {
  return {
    id: guest.id,
    userId: "",
    recipeId: guest.recipeId,
    recipeTitle: guest.recipeTitle,
    recipeEmoji: guest.recipeEmoji,
    stepIndex: guest.stepIndex,
    stepTitle: guest.stepTitle,
    startedAt: guest.startedAt,
    notifyAt: guest.notifyAt,
    status: guest.status,
  };
}

const newId = () =>
  `guest-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export function useRecipeTimer(recipeId?: string) {
  const { user, hasPro } = useAuth();
  const queryClient = useQueryClient();
  // Free accounts (signed in or not) stay local-only; only Pro subscribers
  // get cross-device sync via the API. This is also what makes a timer eligible
  // for the server-side notify-recipe-timers push -- a local-only timer never
  // leaves the device, so it can't be pinged while the app is closed.
  const useLocal = !backendConfigured || !user || !hasPro;
  // The persisted guest cache restores asynchronously; fetching before it finishes
  // would let the empty default overwrite saved data on reload.
  const isRestoring = useIsRestoring();

  const guestQuery = useQuery({
    queryKey: GUEST_TIMERS_QUERY_KEY,
    queryFn: (): GuestTimer[] => [],
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: useLocal && !isRestoring,
  });
  const guestTimersForRecipe = (guestQuery.data ?? [])
    .filter((timer) => timer.recipeId === recipeId)
    .sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1));

  const timersQuery = useQuery({
    queryKey: recipeTimersQueryKey(user?.id ?? "", recipeId ?? ""),
    enabled: Boolean(!useLocal && user && recipeId),
    queryFn: async () => {
      if (!user || !recipeId) return [];
      const list = await unwrap(api.listRecipeTimers());
      return list.items
        .filter((row) => row.recipe_id === recipeId)
        .sort((a, b) => (a.started_at < b.started_at ? 1 : -1))
        .map(mapTimer);
    },
  });

  const startMutation = useMutation({
    mutationFn: async ({
      recipe,
      step,
      stepIndex,
    }: {
      recipe: Recipe;
      step: RecipeStep;
      stepIndex: number;
    }) => {
      if (!user) throw new Error("Must be signed in to use timers");
      if (step.waitMinutes <= 0) throw new Error("Step has no wait time");
      const startedAt = new Date();
      const notifyAt = new Date(
        startedAt.getTime() + step.waitMinutes * 60 * 1000,
      );
      const row = await unwrap(
        api.putRecipeTimer({
          path: { id: Crypto.randomUUID() },
          body: {
            recipe_id: recipe.id,
            recipe_title: recipe.title,
            recipe_emoji: recipe.emoji ?? "🍞",
            step_index: stepIndex,
            step_title: step.title,
            started_at: startedAt.toISOString(),
            notify_at: notifyAt.toISOString(),
            status: "pending",
          },
        }),
      );
      return mapTimer(row);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: recipeTimersQueryKey(user?.id ?? "", recipeId ?? ""),
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (timerId: string) => {
      await unwrap(
        api.patchRecipeTimer({
          path: { id: timerId },
          body: { status: "cancelled" },
        }),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: recipeTimersQueryKey(user?.id ?? "", recipeId ?? ""),
      });
    },
  });

  const data = useLocal
    ? guestTimersForRecipe.map(guestToTimer)
    : (timersQuery.data ?? []);

  return {
    data,
    loading: useLocal ? guestQuery.isPending : timersQuery.isPending,
    error: useLocal
      ? null
      : timersQuery.error
        ? timersQuery.error.message
        : null,
    startStepTimer: async (
      recipe: Recipe,
      step: RecipeStep,
      stepIndex: number,
    ) => {
      if (useLocal) {
        if (step.waitMinutes <= 0) throw new Error("Step has no wait time");
        const startedAt = new Date();
        const notifyAt = new Date(
          startedAt.getTime() + step.waitMinutes * 60 * 1000,
        );
        const timer: GuestTimer = {
          id: newId(),
          recipeId: recipe.id,
          recipeTitle: recipe.title,
          recipeEmoji: recipe.emoji ?? "🍞",
          stepIndex,
          stepTitle: step.title,
          startedAt: startedAt.toISOString(),
          notifyAt: notifyAt.toISOString(),
          status: "pending",
        };
        queryClient.setQueryData<GuestTimer[]>(
          GUEST_TIMERS_QUERY_KEY,
          (current) => [timer, ...(current ?? [])],
        );
        return guestToTimer(timer);
      }
      return startMutation.mutateAsync({ recipe, step, stepIndex });
    },
    cancelTimer: async (timerId: string) => {
      if (useLocal) {
        queryClient.setQueryData<GuestTimer[]>(
          GUEST_TIMERS_QUERY_KEY,
          (current) =>
            (current ?? []).map((timer) =>
              timer.id === timerId
                ? { ...timer, status: "cancelled" as const }
                : timer,
            ),
        );
        return;
      }
      await cancelMutation.mutateAsync(timerId);
    },
    getTimerForStep: (stepIndex: number): RecipeTimer | null =>
      data.find(
        (timer) => timer.stepIndex === stepIndex && timer.status === "pending",
      ) ?? null,
    msRemaining: (timer: RecipeTimer): number =>
      Math.max(0, new Date(timer.notifyAt).getTime() - Date.now()),
    refetch: timersQuery.refetch,
  };
}
